import type { DraftPayment, Payment as PaymentType } from "@/apis/gql/types";
import { useStepper } from "@/components/ui/stepper";
import type { CreatePaymentInput } from "@/queries/payments/create-payment";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Payment } from "./components/payment";
import { PaymentAccountSelection } from "./components/payment-account-selection";
import { PaymentReview } from "./components/payment-review";

type Props = {
  invoiceIds: string[];
  payments: DraftPayment[];
  onAccountSelection: (
    bankAccountId: string,
    bankConnectionId: string,
    payments: PaymentType[],
  ) => Promise<void>;
  webformId: string | null;
  onConfirmPayments: (
    payments: {
      draftPaymentIds: string[];
      input: Partial<CreatePaymentInput>;
    }[],
  ) => Promise<PaymentType[]>;
};

export const PaymentsComponent = ({
  invoiceIds,
  payments: paymentsToReview,
  onAccountSelection,
  webformId,
  onConfirmPayments,
}: Props) => {
  const { t } = useTranslation();

  const { goToStep, prevStep, currentStep } = useStepper();
  const [payments, setPayments] = useState<PaymentType[]>([]);

  const handleBack = () => {
    prevStep();
  };

  const handleReviewConfirm = async (
    draftPayments: {
      draftPaymentIds: string[];
      input: Partial<CreatePaymentInput>;
    }[],
  ) => {
    try {
      const payments = await onConfirmPayments(draftPayments);
      setPayments(payments);
      goToStep(1);
    } catch (error) {
      // biome-ignore lint/complexity/noUselessCatch: capture exception
      throw error;
    }
  };

  const handleAccountSelectionConfirm = async (
    bankAccountId: string,
    bankConnectionId: string,
  ) => {
    await onAccountSelection(bankAccountId, bankConnectionId, payments);
    goToStep(2);
  };

  return (
    <div className="relative overflow-hidden">
      {currentStep === 0 && (
        <PaymentReview
          draftPayments={paymentsToReview}
          onConfirm={handleReviewConfirm}
        />
      )}
      {currentStep === 1 && (
        <PaymentAccountSelection
          payments={payments}
          onBack={handleBack}
          onConfirm={handleAccountSelectionConfirm}
        />
      )}
      {currentStep === 2 && (
        <Payment
          onBack={handleBack}
          onConfirm={() => goToStep(3)}
          webformId={webformId}
          invoiceIds={invoiceIds}
        />
      )}
    </div>
  );
};
